import { OPERATOR_FLOW } from '../../features/operator/operatorConstants';
import { sameAction } from '../../features/operator/operatorIdempotency';
import { closeReceipt, countReceipt } from '../../features/operatorReceiving/operatorReceivingThunks';
import { retryLastPutawayWrite } from '../../features/operatorPutaway/operatorPutawayThunks';
import {
  pickLine,
  reservePickingOrder,
  shortPickLine,
  unpickLine,
} from '../../features/operatorPicking/operatorPickingThunks';
import { ROUTE_WRITES, submitUndoLastPick } from '../../features/operatorPicking/operatorPickingRouteThunks';
import { selectRouteUndoable } from '../../features/operatorPicking/operatorPickingRouteSlice';
import { BAND_ACTION } from '../../features/operatorPicking/operatorPickingRouteSignals';
import {
  requestRelabel,
  retryLastPackingWrite,
  sendToCourier,
} from '../../features/operatorPacking/operatorPackingThunks';
import { selectPackingUnconfirmed } from '../../features/operatorPacking/operatorPackingUtils';
import { dispatchLoad } from '../../features/operatorDispatch/operatorDispatchThunks';
import { KIT_WRITES } from '../../features/operatorKits/operatorKitsThunks';

/*
 * What the band's buttons send. «Reintentar», by what its signal's `retry`
 * carries (see `operatorListeners`): a keyed write travels whole and goes
 * again as it was from any screen; otherwise the flow sends what its slice
 * kept. A second button, by what its signal's `action` names.
 */

/** The writes a band carries whole, by type: their key keeps the server from registering them twice. */
const KEYED_WRITES = {
  [pickLine.typePrefix]: pickLine,
  [shortPickLine.typePrefix]: shortPickLine,
  [unpickLine.typePrefix]: unpickLine,
  [reservePickingOrder.typePrefix]: reservePickingOrder,
  ...ROUTE_WRITES,
  [countReceipt.typePrefix]: countReceipt,
  [closeReceipt.typePrefix]: closeReceipt,
  [dispatchLoad.typePrefix]: dispatchLoad,
  [sendToCourier.typePrefix]: sendToCourier,
  [requestRelabel.typePrefix]: requestRelabel,
  ...KIT_WRITES,
};

/**
 * Sends a keyed write whose answer was lost again, as the band carries it:
 * the same argument under the same key, from whatever screen is on.
 *
 * @param {{type: string, arg: object, key: string}} write
 */
export const retryWrite = (write) => (dispatch) => {
  const thunk = KEYED_WRITES[write.type];
  return thunk ? dispatch(thunk(sameAction(write))) : null;
};

/** The flows whose slice keeps the write to send again. */
export const RETRY_BY_FLOW = {
  [OPERATOR_FLOW.PUTAWAY]: retryLastPutawayWrite,
  [OPERATOR_FLOW.PACKING]: retryLastPackingWrite,
};

/** Packing has no key: its write only goes again from its order's screen. */
const CAN_RETRY_HERE = {
  [OPERATOR_FLOW.PACKING]: (state) => Boolean(selectPackingUnconfirmed(state)),
};

/** The band's `retry`, while it has something to send from the screen on; `null` otherwise. */
export const selectBandRetry = (state) => {
  const retry = state.operatorDevice.signal?.retry;
  if (!retry) return null;
  if (typeof retry !== 'string') return retry;
  return !CAN_RETRY_HERE[retry] || CAN_RETRY_HERE[retry](state) ? retry : null;
};

/** What «Reintentar» dispatches for a band's `retry`. */
export const retryActionOf = (retry) => (typeof retry === 'string' ? RETRY_BY_FLOW[retry]() : retryWrite(retry));

/** Whether what a flow's slice would send again is still on its way, or the screen is reloading. */
const BUSY_BY_FLOW = {
  [OPERATOR_FLOW.PACKING]: ({ operatorPacking: packing }) =>
    Boolean(packing.loading || packing.pendingWrite || packing.opening || packing.inFlightByOrder?.[packing.orderId]),
  [OPERATOR_FLOW.PUTAWAY]: ({ operatorPutaway: putaway }) => Boolean(putaway.pending),
};

/** Whether the band's «Reintentar» is already on its way: a keyed write by its key, which is its request id. */
export const selectBandRetryBusy = (state) => {
  const retry = selectBandRetry(state);
  if (!retry) return false;
  if (typeof retry !== 'string') return Boolean(state.feedback?.inFlight?.[retry.key]);
  return BUSY_BY_FLOW[retry]?.(state) ?? false;
};

/**
 * A band's second button, by its `kind`: whether it still applies, what it
 * sends, and its state while the screen has a write out — `'busy'` when the
 * write is its own, `'blocked'` when it is another.
 */
const BAND_ACTIONS = {
  // «Deshacer» of a take, only on its route's screen and while that take is the last.
  [BAND_ACTION.ROUTE_UNDO]: {
    available: selectRouteUndoable,
    run: () => submitUndoLastPick(),
    status: ({ operatorPickingRoute: visit }) => {
      if (!visit.pending) return 'ready';
      return visit.pending === 'undo' ? 'busy' : 'blocked';
    },
  },
};

/** The band's `action`, while it still applies; `null` otherwise. */
export const selectBandAction = (state) => {
  const action = state.operatorDevice.signal?.action;
  const entry = action && BAND_ACTIONS[action.kind];
  return entry && entry.available(state, action) ? action : null;
};

/** `'ready' | 'busy' | 'blocked'`: whether the band's second button can be tapped, or is on its way. */
export const selectBandActionStatus = (state) => {
  const action = selectBandAction(state);
  return action ? BAND_ACTIONS[action.kind].status(state) : 'ready';
};

/** What the band's second button dispatches. */
export const bandActionOf = (action) => BAND_ACTIONS[action.kind].run(action);
